"use client";

import Image, { StaticImageData } from "next/image";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { memo } from "react";
import Icon, { IconIdType } from "@/components/ui/Icon";
import { Typography } from "@/components/ui/typography";

export type TranslationCardProps = {
  iconId: IconIdType;
  title: string;
  priceLabel?: string;
  buttonPrimaryLabel: string;
  buttonPrimaryHref?: string;
  buttonSecondaryLabel?: string;
  buttonSecondaryHref?: string;
  inheritsFrom?: string;
  features: string[];
  notice?: string;
} & React.ComponentProps<"div">;

const TranslationCard = ({
  title,
  iconId,
  priceLabel,
  buttonPrimaryLabel,
  buttonPrimaryHref = "#",
  buttonSecondaryLabel,
  buttonSecondaryHref = "#",
  inheritsFrom,
  features,
  className,
  notice,
}: TranslationCardProps) => {
  return (
    <div
      className={cn(
        "flex flex-col border bg-white border-neutral-100 rounded-3xl lg:rounded-2xl lg:rounded-2xl overflow-hidden",
        className
      )}
    >
      <div className="flex flex-col p-6 flex-auto sm:flex-initial">
        <div className="grid grid-cols-1 gap-2 lg:gap-3">
          <div className="flex items-center justify-center w-12 h-12 mx-auto lg:w-14 lg:mx-auto lg:w-[105] lg:h-[105] lg:rounded-full lg:bg-[#eaeffb]">
            <Icon
              id={iconId}
              width={58}
              height={58}
              className="size-14.5 shrink-0 text-blue-500"
            />
          </div>
          <Typography
            variant="titleMd"
            asChild
            className="text-center text-semantic-text-primary font-bold md:leading-[150%] md:font-medium md:text-gray-800 lg:text-2xl"
          >
            <h4>{title}</h4>
          </Typography>
        </div>
        {!!priceLabel && (
          <div
            className="mt-2 lg:mt-3 flex items-center justify-center text-2xl font-semibold lg:font-medium text-[#1F559E] lg:min-h-12 [&>span]:text-[32px] [&>span]:font-semibold [&>span]:lg:text-2xl [&>span]:lg:font-medium"
            dangerouslySetInnerHTML={{
              __html: priceLabel,
            }}
          />
        )}
        <div className="mt-6 border-b-1 h-1 border-light-blue md:hidden" />
        <Typography
          variant="labelMd"
          className="mt-6 lg:mt-2 text-semantic-text-primary md:text-gray-800 md:text-sm md:leading-[150%] md:font-normal"
        >
          <Typography
            variant="caption"
            asChild
            className="font-bold tracking-[0.5px] uppercase md:tracking-normal md:normal-case md:text-sm md:leading-[150%] md:font-normal"
          >
            <h4>{inheritsFrom}</h4>
          </Typography>
          <ul className="mt-2 md:mt-3 grid grid-cols-1 gap-3 md:gap-2">
            {features.map((feature, i) => {
              return (
                <li key={i} className="flex gap-2">
                  <Icon
                    id="icon-check_24"
                    width={24}
                    height={24}
                    className="w-6 h-6 text-blue shrink-0"
                  />
                  <span>{feature}</span>
                </li>
              );
            })}
          </ul>
        </Typography>
        {!!notice && (
          <div className="mt-6 pt-6 border-t border-t-light-blue text-sm leading-[160%] text-gray-800 md:border-t-0 lg:leading-[150%] md:mt-2 md:pt-0 text-center">
            <p>{notice}</p>
          </div>
        )}
        <div className="mt-auto grid grid-cols-1 gap-4 pt-6 md:pt-3">
          <div className="flex justify-center">
            <Button
              asChild
              variant="primary"
              className="flex-auto min-w-0 h-[48] md:max-w-[197]"
            >
              <Link href={buttonPrimaryHref}>{buttonPrimaryLabel}</Link>
            </Button>
          </div>
          {!!buttonSecondaryLabel && (
            <div className="flex justify-center">
              <Button
                asChild
                variant="primary-outline"
                className="flex-auto min-w-0 h-[48] md:max-w-[197]"
              >
                <Link href={buttonSecondaryHref}>{buttonSecondaryLabel}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TranslationCard);
